import { useEffect, useState } from "react";
import DataHelper from "@/utils/dataHelper.tsx";
import { useTranslation } from "react-i18next";

const useFetch = <T, >(
  url: string,
  immediately: boolean = false,
  keepalive: boolean = false,
) => {
  const [loading, setLoading] = useState<boolean>(immediately);
  const [errors, setErrors] = useState<string[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [data, setData] = useState<T | null>(null);
  // The url that produced the current `data`, so callers can verify freshness.
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const { t } = useTranslation();

  useEffect(
    () => {
      if (!immediately) {
        setLoading(false);
        return;
      }

      const controller = new AbortController();
      const signal = controller.signal;

      setLoading(true);
      setErrors([]);
      setWarnings([]);

      DataHelper.get(new URL(url), signal, keepalive)
        .then(result => {
          setData(result);
          setDataUrl(url);
          if (result.errors) {
            setErrors(result.errors);
          }
          if (result.warnings) {
            setWarnings(result.warnings);
          }
        })
        .catch(() => {
          if (signal.aborted) {
            return;
          }
          setData(null);
          setDataUrl(url);
          setErrors([t(`errors.fetch`)]);
        })
        .finally(() => {
          if (signal.aborted) {
            return;
          }
          setLoading(false);
        });

      return () => controller.abort('Dismounted');
    },
    [url, immediately]
  );

  return { data, loading, errors, warnings, dataUrl };
}

export default useFetch;
